import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { ReactComponent as ExportIcon } from "assets/images/icons/icn_btn_export.svg";
import { v4 as uuidv4 } from "uuid";
import useDataSourceManager from "hooks/useDataSourceManager";

import InfoCardField from "components/cards/InfoCardField";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { IconInputField } from "components/InputField";

const isValidIpOrDomain = (val) => {
  if (!val || !val.trim()) return false;
  const trimmed = val.trim();
  const ipv4Regex =
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  const fqdnRegex =
    /^(?!-)(?:[a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}$/;
  return ipv4Regex.test(trimmed) || fqdnRegex.test(trimmed);
};

const ToolsContent = ({ auth, infoData, portData }) => {
  const theme = useTheme();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [pingUuid, setPingUuid] = useState(uuidv4());

  // Ping Tool State
  const [destination, setDestination] = useState("");
  const [isPinging, setIsPinging] = useState(false);
  const [hasStartedPing, setHasStartedPing] = useState(false);
  const [logs, setLogs] = useState([]);
  const consoleEndRef = useRef(null);

  const isInputEmpty = !destination.trim();
  const isValidInput = isValidIpOrDomain(destination);
  const isInputError = !isInputEmpty && !isValidInput;

  // Cable Diagnostics State
  const [diagnosticsUuid, setDiagnosticsUuid] = useState(uuidv4());
  const [selectedPorts, setSelectedPorts] = useState([]);
  const [isDiagnosticsRunning, setIsDiagnosticsRunning] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState(null);

  // Extract all ports and linkUp status from portData
  const allPorts = useMemo(() => {
    if (!portData || !Array.isArray(portData)) return [];

    const formattedPorts = portData.map((port) => ({
      id: port.portStaIndex,
      name: `${port.portStaIndex}`,
      isLinkUp: port.portStaStatus === 1,
    }));

    return formattedPorts.sort((a, b) => a.id - b.id);
  }, [portData]);

  // Ping Handlers
  const handleStartPing = () => {
    const targetIp = destination.trim() || "8.8.8.8";
    if (!destination.trim()) {
      setDestination("8.8.8.8");
    }

    const newUuid = uuidv4();
    setPingUuid(newUuid);

    setIsPinging(true);
    setHasStartedPing(true);
    setLogs([`Pinging ${targetIp} with 32 bytes of data:`]);

    const standalone = !auth.orgs?.id || Object.keys(auth.orgs || {}).length === 0;
    const apiPingUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/devices/:deviceId/executeCommand.update`;
    const restfulParams = standalone
      ? {
        orgId: auth.realm?.id,
        siteId: auth.scopeLevel === "org" ? infoData.siteId : auth.sites?.id,
        deviceId: infoData.macAddress,
      }
      : {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.scopeLevel === "org" ? infoData.siteId : auth.sites?.id,
        deviceId: infoData.macAddress,
      };

    sendRequest(apiPingUrl, newUuid, restfulParams, {
      data: {
        command: "ping",
        params: {
          dest: targetIp,
        },
      },
    });
  };

  useEffect(() => {
    const response = getData(pingUuid);
    if (response) {
      const result = response.result || response;
      const error = result?.error || response.error;
      if (error) {
        setLogs([`Error: ${error.message || "Failed to execute ping."}`]);
        setIsPinging(false);
      } else if (result && result.status === "success") {
        const base64Data = result.data;
        try {
          const decodedLog = atob(base64Data);
          const targetIp = destination.trim() || "8.8.8.8";
          const headerLine = `Pinging ${targetIp} with 32 bytes of data:`;
          setLogs([headerLine, ...decodedLog.split("\n")]);
        } catch (e) {
          setLogs(["Error decoding output", base64Data]);
        }
        setIsPinging(false);
      }
    }
  }, [dataMap, pingUuid]);

  const handleClearLog = () => {
    setLogs([]);
    setHasStartedPing(false);
  };

  const handleExportReport = () => {
    if (logs.length === 0) return;
    let reportText = logs.join("\n");

    const blob = new Blob([reportText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "export.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Cable Diagnostics Handlers
  const handleTogglePort = (portId) => {
    if (isDiagnosticsRunning) return;
    setSelectedPorts((prev) =>
      prev.includes(portId)
        ? prev.filter((id) => id !== portId)
        : [...prev, portId]
    );
  };

  const handleRunDiagnostics = () => {
    if (selectedPorts.length === 0) return;
    const newUuid = uuidv4();
    setDiagnosticsUuid(newUuid);
    setIsDiagnosticsRunning(true);
    setDiagnosticResults(null);

    const standalone = !auth.orgs?.id || Object.keys(auth.orgs || {}).length === 0;
    const apiDiagnosticsUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/devices/:deviceId/executeCommand.update`;
    const restfulParams = standalone
      ? {
        orgId: auth.realm?.id,
        siteId: auth.scopeLevel === "org" ? infoData.siteId : auth.sites?.id,
        deviceId: infoData.macAddress,
      }
      : {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.scopeLevel === "org" ? infoData.siteId : auth.sites?.id,
        deviceId: infoData.macAddress,
      };

    sendRequest(apiDiagnosticsUrl, newUuid, restfulParams, {
      data: {
        command: "cableDiagnostic",
        params: {
          ports: selectedPorts,
        },
      },
    });
  };

  useEffect(() => {
    const response = getData(diagnosticsUuid);
    if (response) {
      const result = response.result || response;
      const error = result?.error || response.error;
      if (error) {
        setIsDiagnosticsRunning(false);
      } else if (result && result.status === "success" && Array.isArray(result.data)) {
        const mapStatus = (code) => {
          switch (code) {
            case 0:
              return "OK";
            case 1:
              return "Open";
            case 2:
              return "Short";
            case 3:
              return "Test Failed";
            case 4:
              return "Not Available";
            default:
              return "Unknown";
          }
        };

        const results = result.data.map((item) => {
          const portNum = item.cableDiagTestPortPair;
          const statusVal = item.cableDiagTestResultPair1;
          const len = item.cableDiagTestLenPair1;

          return {
            port: `Port ${portNum}`,
            status: mapStatus(statusVal),
            distance: (len === undefined || len === null || len < 0) ? "-" : `${len}m`,
          };
        });
        setDiagnosticResults(results);
        setIsDiagnosticsRunning(false);
      }
    }
  }, [dataMap, diagnosticsUuid]);

  const handleClearDiagnostics = () => {
    setDiagnosticResults(null);
  };

  const handleExportDiagnostics = () => {
    if (!diagnosticResults) return;
    let reportText = `Cable Diagnostics Report\n`;
    reportText += `--------------------------------------------------\n`;
    reportText += `Port\tStatus\t\tDistance\n`;
    diagnosticResults.forEach((res) => {
      reportText += `${res.port}\t${res.status}\t${res.distance}\n`;
    });

    const blob = new Blob([reportText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "export.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (consoleEndRef.current) {
      consoleEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 2,
        boxSizing: "border-box",
        gap: "20px",
      }}
    >
      {/* 1. Ping Tool Card */}
      <Box width="100%" padding="16px">
        <InfoCardField
          mode="stack"
          width={560}
          title="Ping Tool"
          infoData={
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[1],
                }}
              >
                Check the reachability of a host on an IP network and measure the
                round-trip time for messages.
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: theme.typography.customFontSize[2],
                    fontWeight: theme.typography.customFontWeight[1],
                  }}
                >
                  Destination (FQDN/IPv4)
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  <IconInputField
                    placeholder="e.g. 8.8.8.8"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    disabled={isPinging}
                    error={isInputError}
                    startIcon={
                      <LanguageIcon
                        sx={{ color: theme.palette.primary.greyPrimary }}
                      />
                    }
                  />
                  <OutlineButton
                    onClick={handleStartPing}
                    disabled={isPinging || !isValidInput}
                    sx={{
                      borderRadius: "20px",
                      height: "30px",
                      width: "90px",
                      minWidth: "90px",
                      textTransform: "none",
                      fontWeight: "bold",
                    }}
                  >
                    PING
                  </OutlineButton>
                </Box>
              </Box>

              {/* Live Output Section */}
              {hasStartedPing && (
                <>
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: theme.typography.customFontSize[2],
                          fontWeight: theme.typography.customFontWeight[1],
                        }}
                      >
                        Live Output:
                      </Typography>
                      <Button
                        onClick={handleClearLog}
                        disabled={isPinging}
                        disableRipple
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: theme.typography.customFontSize[2],
                          fontWeight: theme.typography.customFontWeight[3],
                          fontFamily: theme.typography.fontFamily,
                          textDecoration: "underline",
                          textTransform: "none",
                          p: 0,
                          minWidth: "auto",
                          lineHeight: 1,
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "transparent",
                            textDecoration: "underline",
                            color: theme.palette.primary.mainHover,
                          },
                          "&:disabled": {
                            color: theme.palette.action.disabled,
                            textDecoration: "none",
                          },
                        }}
                      >
                        Clear Log
                      </Button>
                    </Box>

                    {/* Dark Console */}
                    <Box
                      sx={{
                        backgroundColor: "#333333",
                        color: "#FFFFFF",
                        borderRadius: "8px",
                        p: 2.5,
                        minHeight: "200px",
                        maxHeight: "320px",
                        overflowY: "auto",
                        fontFamily: "monospace, Courier New",
                        fontSize: "13px",
                        lineHeight: 1.7,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                      }}
                    >
                      {logs.map((log, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: idx === 0 ? "8px" : "2px",
                            color: idx === 0 ? "#D0D0D0" : "#FFFFFF",
                          }}
                        >
                          {log}
                        </div>
                      ))}
                      <div ref={consoleEndRef} />
                    </Box>
                  </Box>

                  {/* Export Report Button */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 1,
                    }}
                  >
                    <SolidButton
                      onClick={handleExportReport}
                      disabled={isPinging || logs.length <= 1}
                      width="150px"
                      height="36px"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ExportIcon style={{ width: 16, height: 16 }} />
                        Export Report
                      </Box>
                    </SolidButton>
                  </Box>
                </>
              )}
            </Box>
          }
        />
      </Box>

      {/* 2. Cable Diagnostics Card */}
      {infoData?.deviceType !== "ap" && (
        <Box width="100%" padding="16px">
          <InfoCardField
            mode="stack"
            width={560}
            title="Cable Diagnostics"
            infoData={
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography
                  sx={{
                    color: theme.palette.primary.greyPrimary,
                    fontSize: theme.typography.customFontSize[1],
                  }}
                >
                  Select ports for testing
                </Typography>

                {/* Link Up Ports Selector Grid */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: "center",
                    my: 1,
                  }}
                >
                  {allPorts.map((port) => {
                    const isSelected = selectedPorts.includes(port.id);
                    const isLinkUp = port.isLinkUp;
                    return (
                      <Box
                        key={port.id}
                        onClick={() => handleTogglePort(port.id)}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          cursor: "pointer",
                          opacity: 1,
                        }}
                      >
                        {/* Port Box */}
                        <Box
                          sx={{
                            width: 44,
                            height: 38,
                            borderRadius: "4px",
                            border: `1px solid ${isSelected
                              ? theme.palette.primary.main
                              : "#D9D9D9"
                              }`,
                            backgroundColor: isSelected
                              ? "#424242"
                              : !isLinkUp
                                ? "#F5F5F5"
                                : "#FFFFFF",
                            color: isSelected
                              ? "#FFFFFF"
                              : !isLinkUp
                                ? theme.palette.primary.greyThird
                                : theme.palette.primary.main,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "14px",
                            boxShadow: isSelected
                              ? "0px 2px 4px rgba(0,0,0,0.15)"
                              : "none",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: theme.palette.primary.mainHover,
                            },
                          }}
                        >
                          {port.name}
                        </Box>
                        {/* Link Up Indicator Dot */}
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: isLinkUp ? "#4CAF50" : "transparent",
                            mt: 0.6,
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>

                {/* Legend Row */}
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "#4CAF50",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: theme.palette.primary.greyPrimary,
                      }}
                    >
                      Link Up
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "2px",
                        backgroundColor: "#424242",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: theme.palette.primary.greyPrimary,
                      }}
                    >
                      Selected
                    </Typography>
                  </Box>
                </Box>

                {/* Control Buttons */}
                <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                  <SolidButton
                    onClick={handleRunDiagnostics}
                    disabled={selectedPorts.length === 0 || isDiagnosticsRunning}
                    width="160px"
                    height="36px"
                  >
                    {isDiagnosticsRunning ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Run Diagnostics"
                    )}
                  </SolidButton>

                  <SolidButton
                    onClick={handleClearDiagnostics}
                    disabled={!diagnosticResults || isDiagnosticsRunning}
                    width="130px"
                    height="36px"
                  >
                    Clear Result
                  </SolidButton>
                </Box>

                {/* Diagnostic Result Table */}
                {diagnosticResults && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: theme.typography.customFontSize[2],
                        fontWeight: theme.typography.customFontWeight[1],
                      }}
                    >
                      Diagnostic Result
                    </Typography>

                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ borderRadius: "8px", overflow: "hidden" }}
                    >
                      <Table size="small">
                        <TableHead sx={{ backgroundColor: "#F5F5F5" }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>Port</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Distance
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {diagnosticResults.map((row, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{row.port}</TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  {row.status === "OK" || row.status === "Not Available" ? (
                                    <CheckCircleIcon
                                      sx={{ color: "#4CAF50", fontSize: 18 }}
                                    />
                                  ) : (
                                    <ErrorIcon
                                      sx={{ color: "#F44336", fontSize: 18 }}
                                    />
                                  )}
                                  <Typography sx={{ fontSize: "14px" }}>
                                    {row.status}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{row.distance}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Export Report Button */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                      }}
                    >
                      <SolidButton
                        onClick={handleExportDiagnostics}
                        width="150px"
                        height="36px"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <ExportIcon style={{ width: 16, height: 16 }} />
                          Export Report
                        </Box>
                      </SolidButton>
                    </Box>
                  </Box>
                )}
              </Box>
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default ToolsContent;
